"use client";
import dynamic from "next/dynamic";
import { TransitionRouter } from "next-transition-router";
import { useAnimate, useReducedMotion } from "@/hooks/useFramerMotion";
import { setUpcomingPathname } from "@/hooks/useUpcomingPathname";
import { useRef, useEffect } from "react";
import { useSceneContext } from "./SceneContext";

const Scene = dynamic(() => import("@/components/3D/Scene"), {
  ssr: false,
});
const Stars = dynamic(() => import("@/components/Stars"), {
  ssr: false,
});

export function TransitionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // const framerMotionFeatures = useFramerMotion();
  const [scope, animateScope] = useAnimate();
  const { webGLAvailable } = useSceneContext();
  const shouldReduceMotion = useReducedMotion();
  const footer = useRef<HTMLElement>(null!);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs (not render-scope `let`) so the leave/enter callbacks can write them
  // without tripping react-hooks/immutability; they coordinate one transition.
  const lastHeight = useRef(0);
  const transformCenterY = useRef(0);

  useEffect(() => {
    const footerElement = document.getElementById("footer-inner");
    if (footerElement) footer.current = footerElement;
  }, []);

  return (
    <TransitionRouter
      auto
      leave={async (next, from, to) => {
        setUpcomingPathname(to ?? null);
        lastHeight.current = scope.current.clientHeight;
        // eslint-disable-next-line react-hooks/immutability -- DOM style write on the animate-scope element, not React state
        scope.current.style.height = `${lastHeight.current}px`;
        transformCenterY.current = window.innerHeight / 2 + window.scrollY;
        if (footer.current) {
          animateScope(
            footer.current,
            shouldReduceMotion ? { opacity: 0 } : { opacity: 0, scale: 1.5 },
            { duration: 0.4, ease: "easeInOut" }
          );
        }
        await animateScope(
          scope.current,
          shouldReduceMotion
            ? { opacity: 0 }
            : {
                opacity: 0,
                transformOrigin: `50% ${transformCenterY.current}px`,
                scale: 1.5,
              },
          { duration: 0.4, ease: "easeInOut" }
        );
        next();
      }}
      enter={async (next) => {
        setUpcomingPathname(null);
        transformCenterY.current = window.innerHeight / 2;
        // scope.current.style.opacity = 1;
        await animateScope(
          scope.current,
          shouldReduceMotion
            ? { opacity: 1 }
            : {
                opacity: 1,
                transformOrigin: `50% ${transformCenterY.current}px`,
                scale: 1,
              },
          { duration: 0.4, ease: "easeInOut" }
        );
        // scope.current.style.height = "";

        if (footer.current) {
          animateScope(
            footer.current,
            shouldReduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1 },
            { duration: 0.4, ease: "easeInOut" }
          );
        }

        next();
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          scope.current.style = "";
          if (footer.current) {
            footer.current.style.transform = "";
          }
        }, 50);
      }}>
      <div
        ref={scope}
        className="will-change-auto">
        {children}
      </div>
      {webGLAvailable && <Scene />}
      <Stars />
    </TransitionRouter>
  );
}
